import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { X, Download, Smartphone } from 'lucide-react'
import { useRegisterSW } from 'virtual:pwa-register/react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
  }>
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Register PWA service worker
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log('SW Registered: ' + r)
    },
    onRegisterError(error) {
      console.log('SW registration error', error)
    },
  })

  useEffect(() => {
    // Check if already dismissed in this session
    const sessionDismissed = sessionStorage.getItem('pwa-prompt-dismissed')
    if (sessionDismissed) {
      setDismissed(true)
      return
    }

    const handler = (e: Event) => {
      e.preventDefault()
      console.log('beforeinstallprompt event fired')
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShowPrompt(true)
    }

    window.addEventListener('beforeinstallprompt', handler)

    // Also check if we're on mobile and the app is not installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    
    console.log('PWA Install Prompt - isStandalone:', isStandalone, 'isMobile:', isMobile)
    
    if (isMobile && !isStandalone && !sessionDismissed) {
      // Show after a delay on mobile
      setTimeout(() => {
        console.log('Showing PWA prompt after delay')
        setShowPrompt(true)
      }, 3000)
    }

    // For testing - show prompt after 5 seconds on any device
    if (!sessionDismissed && !isStandalone) {
      setTimeout(() => {
        console.log('Showing PWA prompt for testing after 5 seconds')
        setShowPrompt(true)
      }, 5000)
    }

    // Check if app is already installed
    if (isStandalone) {
      console.log('App is already installed')
      setDismissed(true)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
    }
  }, [])

  const handleInstall = async () => {
    console.log('Install clicked, deferredPrompt:', deferredPrompt)
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt()
        const { outcome } = await deferredPrompt.userChoice
        console.log('User choice:', outcome)
        if (outcome === 'accepted') {
          setShowPrompt(false)
          sessionStorage.setItem('pwa-prompt-dismissed', 'true')
        }
        setDeferredPrompt(null)
      } catch (error) {
        console.error('Error during install prompt:', error)
      }
    } else {
      // Fallback for browsers that don't support beforeinstallprompt
      alert('To install this app:\n1. Open browser menu\n2. Look for "Add to Home Screen" or "Install App"\n3. Follow the prompts')
    }
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    setDismissed(true)
    sessionStorage.setItem('pwa-prompt-dismissed', 'true')
  }

  // Don't show if dismissed or if app is already installed
  if (!showPrompt || dismissed) {
    return null
  }

  const brandGreen = 'rgb(119, 175, 30)'

  return (
    <Card
      className="fixed bottom-4 left-4 right-4 z-50 sm:max-w-md sm:left-auto"
      style={{ backgroundColor: '#000000f0', borderColor: brandGreen }}
    >
      <CardContent className="p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <Smartphone className="h-6 w-6" style={{ color: brandGreen }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-white">
              Install ordore Analytics
            </h3>
            <p className="text-xs text-white/70 mt-1">
              Add to your home screen for quick access and offline use
            </p>
            <div className="flex items-center space-x-2 mt-3">
              <Button
                size="sm"
                onClick={handleInstall}
                className="flex items-center space-x-1"
              >
                <Download className="h-3 w-3" />
                <span>Install</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                style={{ color: brandGreen }}
              >
                Not now
              </Button>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 h-auto"
            style={{ color: brandGreen }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}